import { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { createSelector } from 'reselect'
import { useSelector } from '.'
import { AssetKey } from '../../../types/Assets'
import { HTMLDatasetUrls } from '../../../types/Configuration'
import { ConstelaConfiguration, ConstelaConfigurationModule } from '../../../types/ConstelaConfiguration'
import ImageData from '../../../types/ImageData'
import ModuleData from '../../../types/ModuleData'
import { FilteredPropertyData, FilteredPropertyDataValue } from '../../../types/PropertyData'
import { TranslationKey } from '../../../types/Translations'
import { filterProperties } from '../../model/ConstelaConfiguration'
import { setExpertPropertiesAction } from '../actions/configurationActions'
import { AppState } from '../types'
import { DataState } from '../types/dataTypes'
import { getSystemHeight } from './configurationHooks'

export const useTranslation = (): ((key: TranslationKey) => string) => {
  const translations = useSelector((state) => state.data.translations)
  return useCallback((key: TranslationKey) => translations?.[key] ?? '', [translations])
}

export const useAsset = (key: AssetKey): ImageData | undefined => {
  return useSelector((state) => state.data.assets?.[key])
}

export const useUrl = (key: keyof HTMLDatasetUrls): string | undefined => {
  return useSelector((state) => state.data.urls?.[key])
}

const emptyModuleArray: ModuleData[] = []
export const useAvailableModules = (
  applicationID: string,
  configurationType: ConstelaConfiguration['type']
): ModuleData[] => {
  return useSelector((state) => {
    if (configurationType === 'inspiration') {
      return state.data.applications.find((x) => x.id === applicationID)?.availableModules ?? emptyModuleArray
    }
    return state.data.modules
  })
}

export const globalPropertiesSelector = createSelector<
  AppState,
  Record<string, string>,
  DataState,
  FilteredPropertyData[]
>(
  (state) => state.configuration.expertMode.properties,
  (state) => state.data,
  (configurationProperties, data) => {
    return filterProperties(data, configurationProperties, { module: 'global' })
  }
)

export const useGlobalProperties = (): FilteredPropertyData[] => useSelector(globalPropertiesSelector)
export const usePropertiesForModule = (
  index?: number
): {
  propertyData: Record<string, FilteredPropertyData>
  properties: Record<string, string>
  setProperty: Record<string, (nextValue: string) => void>
} => {
  const data = useSelector((state) => state.data)

  const modulesData = useSelector((state) => state.data.modules)
  const maxHeight = useSelector((state) => state.data.system.maxHeight)
  const configuration = useSelector((state) => state.configuration.expertMode)
  const module = configuration.modules[index ?? -1]
  const globalProps = useSelector((state) => state.configuration.expertMode.properties)

  const checkHeight = useCallback(
    (propertyName: string, propertyValue: string): boolean => {
      const hypotheticalConfig = {
        ...configuration,
        modules: configuration.modules.map(
          (x, i): ConstelaConfigurationModule => {
            if (i !== index) {
              return x
            }
            return {
              ...x,
              properties: {
                ...(x.properties ?? {}),
                [propertyName]: propertyValue
              }
            }
          }
        )
      }
      return getSystemHeight(modulesData, hypotheticalConfig) <= maxHeight
    },
    [configuration, index, maxHeight, modulesData]
  )

  const propertyData = useMemo(() => {
    if (!module || !module?.properties) {
      return []
    }
    return filterProperties(data, globalProps, { ...module.properties, module: module.id }).map((x) => ({
      ...x,
      values: x.values.map(
        (v): FilteredPropertyDataValue =>
          v.disabled ? v : checkHeight(x.id, v.id) ? v : { ...v, disabled: { type: 'disabled', reason: '' } }
      )
    }))
  }, [checkHeight, data, globalProps, module])

  const dispatch = useDispatch()
  const setProperty = useMemo(
    () =>
      Object.fromEntries(
        propertyData.map(({ id }) => [
          id,
          (nextValue: string) => {
            if (typeof index === 'number') {
              dispatch(setExpertPropertiesAction(index, { [id]: nextValue }))
            }
          }
        ])
      ),
    [dispatch, index, propertyData]
  )

  return {
    propertyData: Object.fromEntries(propertyData.map((x) => [x.id, x])),
    properties: module?.properties ?? {},
    setProperty
  }
}
